import type { PluginSourceInfo } from "@magic-md/plugin-sdk";
import { useT } from "@magic-md/i18n/react";
import {
    AlertTriangle,
    CheckCircle2,
    Cloud,
    CloudDownload,
    HardDrive,
    Loader2,
    Package,
    Search,
    X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "../lib/utils";
import {
    buildPluginListSummary,
    countPluginSources,
    getPluginSourceSearchTerms,
    PLUGIN_SOURCE_FILTERS,
    type PluginSourceFilter,
    pluginMatchesSourceFilter,
    pluginSourceFilterLabelKey,
} from "../plugins/plugin-source-metadata";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";
import { PluginSourceBadge } from "./PluginSourceBadge";

export interface PluginListItemModel {
    id: string;
    title: string;
    icon?: string;
    description?: string;
    version?: string;
    actionCount?: number;
    source?: PluginSourceInfo;
    disabled?: boolean;
    readOnlyLabel?: string;
    status?: "ready" | "warning" | "none";
    statusTitle?: string;
    searchText?: string[];
    toggle?: {
        checked: boolean;
        disabled?: boolean;
        loading?: boolean;
        onChange: (checked: boolean) => void;
    };
}

export function PluginList({
    items,
    selectedId,
    onSelect,
    title,
    searchQuery,
    onSearchChange,
    sourceFilter,
    onSourceFilterChange,
    searchPlaceholder,
    emptyMessage,
    noMatchesMessage,
    showSearch = true,
    showSourceFilter = true,
    className,
}: {
    items: PluginListItemModel[];
    selectedId?: string | null;
    onSelect?: (id: string) => void;
    title?: string;
    searchQuery?: string;
    onSearchChange?: (query: string) => void;
    sourceFilter?: PluginSourceFilter;
    onSourceFilterChange?: (filter: PluginSourceFilter) => void;
    searchPlaceholder?: string;
    emptyMessage?: string;
    noMatchesMessage?: string;
    showSearch?: boolean;
    showSourceFilter?: boolean;
    className?: string;
}) {
    const t = useT("editor");
    const [internalSearch, setInternalSearch] = useState("");
    const [internalSourceFilter, setInternalSourceFilter] =
        useState<PluginSourceFilter>("all");
    const activeSearch = searchQuery ?? internalSearch;
    const activeSourceFilter = sourceFilter ?? internalSourceFilter;
    const setSearch = onSearchChange ?? setInternalSearch;
    const setSourceFilter = onSourceFilterChange ?? setInternalSourceFilter;
    const counts = useMemo(() => countPluginSources(items), [items]);
    const filteredItems = useMemo(() => {
        const q = activeSearch.trim().toLowerCase();
        return items.filter((item) => {
            if (!pluginMatchesSourceFilter(item.source, activeSourceFilter)) {
                return false;
            }
            if (!q) return true;
            return buildPluginSearchTerms(item)
                .some((value) => value.toLowerCase().includes(q));
        });
    }, [activeSearch, activeSourceFilter, items]);
    const hasFilters = activeSearch.trim().length > 0 || activeSourceFilter !== "all";

    return (
        <div className={cn("flex min-h-0 flex-1 flex-col", className)}>
            {(showSearch || showSourceFilter || title) ? (
                <div className="border-b border-border px-2 py-2">
                    {title ? (
                        <div className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {title}
                        </div>
                    ) : null}
                    {(showSearch || showSourceFilter) ? (
                        <div className="flex items-center gap-1.5">
                            {showSearch ? (
                                <div className="relative min-w-0 flex-1">
                                    <Search
                                        size={12}
                                        className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground/50"
                                    />
                                    <input
                                        type="text"
                                        className="h-7 w-full rounded-md border border-border bg-background pl-7 pr-7 text-xs placeholder:text-muted-foreground/40 outline-none transition-colors focus:border-primary"
                                        placeholder={searchPlaceholder ?? t("installedPlugins.searchPlaceholder")}
                                        value={activeSearch}
                                        onChange={(event) => setSearch(event.target.value)}
                                    />
                                    {activeSearch ? (
                                        <button
                                            type="button"
                                            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground/40 transition-colors hover:text-foreground"
                                            onClick={() => setSearch("")}
                                        >
                                            <X size={12} />
                                        </button>
                                    ) : null}
                                </div>
                            ) : null}
                            {showSourceFilter ? (
                                <div className="inline-flex shrink-0 items-center rounded-md border border-border/70 bg-muted/20 p-0.5">
                                    {PLUGIN_SOURCE_FILTERS.map((filter) => {
                                        const active = activeSourceFilter === filter;
                                        const count = counts[filter];
                                        return (
                                            <button
                                                key={filter}
                                                type="button"
                                                onClick={() => setSourceFilter(filter)}
                                                className={cn(
                                                    "inline-flex h-6 min-w-8 shrink-0 items-center justify-center gap-1 rounded px-1.5 text-[10px] transition-colors",
                                                    active
                                                        ? "bg-background text-primary shadow-sm"
                                                        : "text-muted-foreground hover:bg-background/70 hover:text-foreground",
                                                )}
                                                title={t(pluginSourceFilterLabelKey(filter))}
                                                aria-label={t(pluginSourceFilterLabelKey(filter))}
                                            >
                                                <SourceFilterIcon filter={filter} />
                                                <span className="tabular-nums opacity-70">{count}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            ) : null}

            <div className="min-h-0 flex-1 overflow-auto">
                {filteredItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-2 p-4 text-center">
                        <Package size={16} className="text-muted-foreground/40" />
                        <p className="text-[10px] text-muted-foreground/50">
                            {hasFilters
                                ? (noMatchesMessage ?? t("installedPlugins.noMatching"))
                                : (emptyMessage ?? t("installedPlugins.empty"))}
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-border/30">
                        {filteredItems.map((item) => (
                            <PluginListItem
                                key={item.id}
                                item={item}
                                selected={selectedId === item.id}
                                onSelect={onSelect}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function SourceFilterIcon({ filter }: { filter: PluginSourceFilter }) {
    if (filter === "cloud-worker") return <Cloud size={12} />;
    if (filter === "cloud-bundle") return <CloudDownload size={12} />;
    if (filter === "local") return <HardDrive size={12} />;
    return <Package size={12} />;
}

function PluginListItem({
    item,
    selected,
    onSelect,
}: {
    item: PluginListItemModel;
    selected: boolean;
    onSelect?: (id: string) => void;
}) {
    const t = useT("editor");
    const summary = buildPluginListSummary(item, t);
    const interactive = !!onSelect && !item.disabled;

    return (
        <button
            type="button"
            onClick={() => {
                if (interactive) onSelect(item.id);
            }}
            className={cn(
                "flex w-full items-start gap-2 px-3 py-1.5 text-left transition-colors",
                selected ? "bg-primary/5" : interactive ? "hover:bg-muted/40" : "cursor-default",
                item.disabled && "opacity-75",
            )}
        >
            {item.toggle ? (
                <label
                    className="mt-0.5 flex items-center"
                    onClick={(event) => event.stopPropagation()}
                >
                    <input
                        type="checkbox"
                        checked={item.toggle.checked}
                        disabled={item.toggle.disabled || item.toggle.loading}
                        onChange={(event) => item.toggle?.onChange(event.target.checked)}
                    />
                </label>
            ) : null}
            <span className="mt-0.5 shrink-0 text-sm leading-none">
                {item.icon ?? "🧩"}
            </span>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <div className="flex min-w-0 flex-1 items-center gap-1.5">
                        <span className="truncate text-xs font-medium text-foreground/80">
                            {item.title}
                        </span>
                        <StatusIcon item={item} />
                        {item.readOnlyLabel ? (
                            <span className="shrink-0 rounded border border-border/60 bg-muted/40 px-1 py-0.5 text-[10px] text-muted-foreground">
                                {item.readOnlyLabel}
                            </span>
                        ) : null}
                        {item.toggle?.loading ? (
                            <Loader2 size={12} className="shrink-0 animate-spin text-muted-foreground" />
                        ) : null}
                    </div>
                    <PluginSourceBadge
                        source={item.source}
                        compact
                        className="ml-auto"
                    />
                </div>
                {summary.text ? (
                    <div
                        className="mt-1 truncate text-xs text-muted-foreground"
                        title={summary.title || summary.text}
                    >
                        {summary.text}
                    </div>
                ) : null}
            </div>
        </button>
    );
}

function StatusIcon({ item }: { item: PluginListItemModel }) {
    if (!item.status || item.status === "none") return null;
    if (item.status === "warning") {
        return (
            <span title={item.statusTitle} className="inline-flex shrink-0">
                <AlertTriangle
                    size={10}
                    className="theme-accent-text"
                    style={getThemeAccentStyle("warning")}
                />
            </span>
        );
    }
    return (
        <span title={item.statusTitle} className="inline-flex shrink-0">
            <CheckCircle2
                size={10}
                className="theme-accent-text"
                style={getThemeAccentStyle("success")}
            />
        </span>
    );
}

function buildPluginSearchTerms(item: PluginListItemModel): string[] {
    return [
        item.id,
        item.title,
        item.description,
        item.version,
        item.actionCount == null ? undefined : String(item.actionCount),
        ...getPluginSourceSearchTerms(item.source),
        ...(item.searchText ?? []),
    ].filter((value): value is string => !!value);
}
